import { useState, useRef, useEffect } from "react";
import { ReactComponent as CameraIcon } from "../../assets/icons/camera-icon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close-icon.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-feedback.svg";
import { ReactComponent as ScenarioIcon } from "../../assets/icons/scenario.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/email.svg";
import { ReactComponent as ChatDotsIcon } from "../../assets/icons/chat-dots.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/clock.svg";
import { ReactComponent as VideoIcon } from "../../assets/icons/video.svg";

import { answerHelpCase } from "../../service/helpCaseService";

import {
  ModalOverlay,
  ModalContainer,
  RequestDetailCard,
  RequestDetailHeader,
  RequestDetailTitle,
  CloseButton,
  RequestDetailInfo,
  InfoItem,
  RequestDetailBody,
  RequestLabel,
  RequestVideoWrapper,
  RequestVideo,
  RequestContent,
  ReplySection,
  ReplyTabs,
  ReplyTab,
  ReplyTabContent,
  TextArea,
  VideoUploadArea,
  VideoUploadIcon,
  VideoUploadText,
  VideoActionButtons,
  VideoActionButton,
  VideoPreview,
  ReplyFooter,
  CancelButton,
  SendButton,
  Toast,
} from "./styles";

interface ReplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  helpCaseId: string;
  email: string;
  date: string;
  scenarioName?: string;
  content?: string;
  videoUrl?: string;
  onSuccess?: () => void;
}

export function ReplyModal({
  isOpen,
  onClose,
  helpCaseId,
  email,
  date,
  scenarioName,
  content,
  videoUrl,
  onSuccess,
}: ReplyModalProps) {
  const [activeTab, setActiveTab] = useState<"text" | "video">("text");
  const [replyText, setReplyText] = useState("");
  const [replyVideoFile, setReplyVideoFile] = useState<File | null>(null);
  const [replyVideoPreview, setReplyVideoPreview] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    if (cameraStream && liveVideoRef.current) {
      liveVideoRef.current.srcObject = cameraStream;
      liveVideoRef.current.play().catch(() => {});
    }
  }, [cameraStream]);

  if (!isOpen) return null;

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReplyVideoFile(file);
      const url = URL.createObjectURL(file);
      setReplyVideoPreview(url);
    }
  };

  const handleRecordVideo = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: true,
      });

      setIsRecording(true);
      setCameraStream(stream);

      setTimeout(() => {
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream;
          liveVideoRef.current.play().catch(() => {});
        }
      }, 100);

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const file = new File([blob], "gravacao-resposta.webm", { type: "video/webm" });
        setReplyVideoFile(file);
        setReplyVideoPreview(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
        setCameraStream(null);
        if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
      };

      mediaRecorder.start();
    } catch {
      showToast("Não foi possível acessar a câmera. Verifique as permissões.", "error");
      setIsRecording(false);
    }
  };

  const handleSendReply = async () => {
    if (isSending) return;

    try {
      setIsSending(true);

      if (replyVideoFile) {
        await answerHelpCase(parseInt(helpCaseId), email, replyText || "", replyVideoFile);
      } else {
        await answerHelpCase(parseInt(helpCaseId), email, replyText);
      }

      showToast("Resposta enviada com sucesso por e-mail!", "success");
      setTimeout(() => {
        onSuccess?.();
        handleCloseModal();
      }, 5000);
    } catch (error: any) {
      const apiMessage = error.response?.data?.message || error.response?.data?.error;
      showToast(apiMessage || "Não foi possível enviar a resposta. Tente novamente.", "error");
    } finally {
      setIsSending(false);
    }
  };

  const handleCloseModal = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    
    setReplyText("");
    setReplyVideoFile(null);
    if (replyVideoPreview) {
      URL.revokeObjectURL(replyVideoPreview);
    }
    setReplyVideoPreview(null);
    setIsRecording(false);
    setActiveTab("text");
    onClose();
  };

  const canSend =
    !isSending &&
    (replyVideoFile !== null || replyText.trim().length > 0);

  return (
    <>
    {toast && <Toast type={toast.type}>{toast.message}</Toast>}
    <ModalOverlay onClick={handleCloseModal}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <RequestDetailCard>
          <RequestDetailHeader>
            <RequestDetailTitle>
              <CameraIcon className="icon" />
              Dúvida de {email}
            </RequestDetailTitle>
            <CloseButton onClick={handleCloseModal}>
              <CloseIcon className="icon" />
            </CloseButton>
          </RequestDetailHeader>

          <RequestDetailInfo>
            <InfoItem>
              <UserIcon className="icon" />
              {email}
              
            </InfoItem>
            <InfoItem>
              <ClockIcon className="icon" />
              {date}
            </InfoItem>
            {scenarioName && (
              <InfoItem>
                <ChatDotsIcon className="icon" />
                {scenarioName}
              </InfoItem>
            )}

          </RequestDetailInfo>

          <RequestDetailBody>
            <RequestLabel>Dúvida: {content}</RequestLabel>

            {videoUrl && (
              <RequestVideoWrapper>
                <RequestVideo src={videoUrl} controls>
                  Seu navegador não suporta vídeo.
                </RequestVideo>
              </RequestVideoWrapper>
            )}

          </RequestDetailBody>
        </RequestDetailCard>

        {/* Seção de resposta */}
        <ReplySection>
          <ReplyTabs>
            <ReplyTab
              isActive={activeTab === "text"}
              onClick={() => setActiveTab("text")}
            >
              <ScenarioIcon style={{filter: 'invert(1)', height: '1rem', width: '1rem'}}/> Texto
            </ReplyTab>
            <ReplyTab
              isActive={activeTab === "video"}
              onClick={() => setActiveTab("video")}
            >
              <CameraIcon />Vídeo
            </ReplyTab>
          </ReplyTabs>

          <ReplyTabContent>
            {activeTab === "text" ? (
              <TextArea
                placeholder="Digite sua resposta aqui..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
              />
            ) : isRecording ? (
              <VideoPreview
                key="live-video"
                ref={liveVideoRef}
                autoPlay
                muted
                playsInline
                onLoadedMetadata={(e) => {
                  (e.target as HTMLVideoElement).play().catch(() => {});
                }}
              />
            ) : replyVideoPreview ? (
              <VideoPreview 
                key="recorded-video"
                src={replyVideoPreview} 
                controls 
                autoPlay
              />
            ) : (
              <VideoUploadArea>
                <VideoUploadIcon>
                  <CameraIcon className="icon" />
                </VideoUploadIcon>
                <VideoUploadText>
                  Grave ou carregue um vídeo de resposta
                </VideoUploadText>
              </VideoUploadArea>
            )}
          </ReplyTabContent>

          {activeTab === "video" && (
            <VideoActionButtons>
              <VideoActionButton onClick={handleRecordVideo} primary>
                <VideoIcon className="icon" style={{ height: '1rem', width: '1rem'}} />
                {isRecording ? "Parar Gravação" : "Gravar Vídeo"}
              </VideoActionButton>
              <VideoActionButton onClick={handleFileUpload}>
                <UploadIcon className="icon" />
                Carregar Arquivo
              </VideoActionButton>
            </VideoActionButtons>
          )}

          <input
            type="file"
            ref={fileInputRef}
            accept="video/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <ReplyFooter>
            <CancelButton onClick={handleCloseModal} disabled={isSending}>
              Cancelar
            </CancelButton>
            <SendButton onClick={handleSendReply} disabled={!canSend}>
              <EmailIcon className="icon" />
              {isSending ? "Enviando..." : "Enviar por E-mail"}
            </SendButton>
          </ReplyFooter>
        </ReplySection>
      </ModalContainer>
    </ModalOverlay>
    </>
  );
}
